import React from "react";
import { Box, Typography, Tooltip, Collapse } from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import {
  BLUE,
  BLUE_PALE,
  BORDER,
  INK,
  INK_MUTED,
  SUCCESS,
  SURFACE,
  WARN,
} from "./shared";

// ─── Section Header ──────────────────────────────────────────────────────────
// ─── Section Card (collapsible accordion) ────────────────────────────────────
// Props:
//   collapsed  — boolean: if undefined the section is always open (legacy)
//   onToggle   — fn: called when header is clicked
//   summary    — string: shown in header when collapsed
//   readOnly   — boolean: makes the section content inert (view-only)
const SectionCard = ({
  icon,
  title,
  children,
  action,
  noPad,
  step,
  stepState,
  collapsed,
  onToggle,
  summary,
  readOnly,
}) => {
  const isCollapsible = typeof collapsed !== "undefined";

  // When read-only, the section CONTENT is made inert (non-clickable,
  // non-focusable, non-editable — this covers MUI Selects/Autocompletes too)
  // while the header stays live so the section can still be collapsed/expanded.
  // `inert` is set directly on the DOM node via a ref (reliable on React 18,
  // which doesn't treat `inert` as a known prop); pointerEvents/opacity are a
  // visual cue and a fallback for engines without inert support.
  const setContentInert = (node) => {
    if (node) node.inert = !!readOnly;
  };
  const contentGuard = readOnly
    ? {
        ref: setContentInert,
        style: { pointerEvents: "none", opacity: 0.85 },
      }
    : { ref: setContentInert };

  // The step badge + title. This — NOT the whole header bar — is what carries
  // the "Open/Close section" tooltip, so that the action buttons on the right
  // of the bar are outside its anchor and only ever show their own tooltip.
  const titleGroup = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
      {step ? (
        <Box
          sx={{
            width: 22,
            height: 22,
            borderRadius: "50%",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "0.72rem",
            fontWeight: 700,
            color: "#fff",
            transition: "background 0.2s",
            bgcolor:
              stepState === "done"
                ? SUCCESS
                : stepState === "active"
                ? WARN
                : "#CBD5E1",
          }}
        >
          {stepState === "done" ? (
            <CheckCircleIcon sx={{ fontSize: 15 }} />
          ) : (
            step
          )}
        </Box>
      ) : (
        <Box sx={{ width: 3, height: 18, bgcolor: BLUE, borderRadius: 4 }} />
      )}
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: "0.875rem",
          color: INK,
          letterSpacing: "-0.01em",
          display: "flex",
          alignItems: "center",
          gap: 0.75,
        }}
      >
        <span style={{ fontSize: "0.95rem" }}>{icon}</span>
        {title}
      </Typography>
    </Box>
  );

  const headerContent = (
    <>
      {isCollapsible ? (
        <Tooltip
          title={collapsed ? "Open section" : "Close section"}
          placement="bottom-start"
        >
          {titleGroup}
        </Tooltip>
      ) : (
        titleGroup
      )}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          minWidth: 0,
          flexShrink: 1,
        }}
      >
        {isCollapsible && collapsed && summary && (
          <Typography
            variant="caption"
            sx={{
              color: INK_MUTED,
              fontSize: "0.78rem",
              minWidth: 0,
              maxWidth: { xs: "100%", sm: 360 },
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {summary}
          </Typography>
        )}
        {action && <Box onClick={(e) => e.stopPropagation()}>{action}</Box>}
        {isCollapsible && (
          <ExpandMoreIcon
            sx={{
              color: INK_MUTED,
              fontSize: 20,
              transform: collapsed ? "rotate(0deg)" : "rotate(180deg)",
              transition: "transform 0.2s ease",
              flexShrink: 0,
            }}
          />
        )}
      </Box>
    </>
  );

  if (isCollapsible) {
    return (
      <Box sx={{ mb: 1.5 }}>
        {/* Clickable header bar — click anywhere on it to open/close. The
            tooltip sits on the title group only (see above), never on the bar
            itself, so the action buttons don't inherit it. */}
        <Box
          onClick={onToggle}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            p: "8px 14px",
            bgcolor: SURFACE,
            border: `1px solid ${BORDER}`,
            borderRadius: collapsed ? "12px" : "12px 12px 0 0",
            cursor: "pointer",
            boxShadow: collapsed
              ? "0 1px 3px rgba(15,23,42,0.05)"
              : "0 1px 2px rgba(15,23,42,0.03)",
            transition: "background 0.15s",
            "&:hover": { bgcolor: BLUE_PALE },
          }}
        >
          {headerContent}
        </Box>
        {/* Collapsible content */}
        <Collapse in={!collapsed} timeout={200} unmountOnExit>
          <Box
            {...contentGuard}
            sx={{
              bgcolor: SURFACE,
              border: `1px solid ${BORDER}`,
              borderTop: "none",
              borderRadius: "0 0 12px 12px",
              p: noPad ? 0 : 1.5,
              boxShadow: "0 2px 6px rgba(15,23,42,0.06)",
              overflow: "hidden",
            }}
          >
            {children}
          </Box>
        </Collapse>
      </Box>
    );
  }

  // Non-collapsible fallback (legacy use)
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1.5,
        }}
      >
        {headerContent}
      </Box>
      <Box
        {...contentGuard}
        sx={{
          bgcolor: SURFACE,
          border: `1px solid ${BORDER}`,
          borderRadius: "12px",
          p: noPad ? 0 : 1.5,
          boxShadow: "0 1px 3px rgba(15,23,42,0.05)",
          overflow: "hidden",
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default SectionCard;
